class QuizModalController {
  constructor() {
    this.modal = document.getElementById('QuizModal');
    this.contentTarget = document.getElementById('QuizModalContent');
    this.sectionId = 'quiz-main';
    this.isLoading = false;
    this.loadedOnce = false;
    this.onBodyKeydown = this.onBodyKeydown.bind(this);
    this.onClickOpen = this.onClickOpen.bind(this);
    this.onModalClosed = this.onModalClosed.bind(this);
  }

  init() {
    if (!this.modal || !this.contentTarget) return;

    document.body.addEventListener('click', this.onClickOpen);
    document.body.addEventListener('modalClosed', this.onModalClosed);
  }

  onClickOpen(event) {
    const triggerAttr = event.target.closest('[data-js-open-quiz]');
    const anchor = event.target.closest('a[href]');
    let shouldOpen = !!triggerAttr;
    if (!shouldOpen && anchor) {
      const href = anchor.getAttribute('href') || '';
      try {
        const url = new URL(href, window.location.origin);
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const expected = `${root}pages/quiz?section=quiz-main`;
        const normalize = (p) => p.replace(/\/+$/, '');
        if (normalize(url.pathname) === normalize(expected)) shouldOpen = true;
      } catch {}
    }
    if (!shouldOpen) return;
    
    // Prevent default behavior and stop propagation
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    this.open();
  }

  async open() {
    if (!this.modal || !this.contentTarget) return;
    
    // Save current scroll position
    this.savedScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    if (!this.loadedOnce && !this.isLoading) {
      await this.loadSection();
    }
    this.modal.show(document.body);
    document.body.classList.add('quiz-modal-open');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.savedScrollTop}px`;
    document.body.style.width = '100%';
    
    document.addEventListener('keydown', this.onBodyKeydown, { once: true });
  }

  async loadSection() {
    this.isLoading = true;
    try {
      const base = window.Shopify?.routes?.root || window.location.pathname;
      const url = `${base}?sections=${encodeURIComponent(this.sectionId)}`;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Failed to load section: ${response.status}`);
      const json = await response.json();
      const html = json[this.sectionId];
      if (!html) throw new Error('Section payload missing');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sectionEl = parsed.querySelector('#shopify-section-' + this.sectionId);
      const inner = sectionEl ? sectionEl.innerHTML : html;
      await this.injectContent(inner);
      this.hookCloseButton();
      this.loadedOnce = true;
    } catch (e) {
      console.error('[QuizModal] load error', e);
      this.contentTarget.innerHTML = '';
    } finally {
      this.isLoading = false;
    }
  }

  async injectContent(markup) {
    await this.insertHTMLWithScripts(this.contentTarget, markup);
  }

  async insertHTMLWithScripts(element, html) {
    element.innerHTML = html;
    const scripts = Array.from(element.querySelectorAll('script'));
    window.NaturawQuizLoadedSrcs = window.NaturawQuizLoadedSrcs || new Set();
    for (const oldScript of scripts) {
      const srcAttr = oldScript.getAttribute('src');
      const typeAttr = (oldScript.getAttribute('type') || '').trim();
      if (!srcAttr) {
        if (typeAttr && typeAttr !== 'text/javascript' && typeAttr !== 'application/javascript' && typeAttr !== 'module') { continue; }
        const inlineScript = document.createElement('script');
        if (typeAttr) inlineScript.type = typeAttr;
        for (const { name, value } of Array.from(oldScript.attributes)) inlineScript.setAttribute(name, value);
        inlineScript.textContent = oldScript.textContent || '';
        oldScript.parentNode.replaceChild(inlineScript, oldScript);
        continue;
      }
      let resolvedSrc = oldScript.src;
      try {
        const urlObj = new URL(resolvedSrc, document.baseURI);
        if (urlObj.hostname === window.location.hostname && urlObj.pathname.startsWith('/assets/')) {
          const fileName = urlObj.pathname.split('/').pop();
          const base = this.getAssetsBaseUrl();
          if (base && fileName) resolvedSrc = base + fileName;
        }
      } catch {}
      if (window.NaturawQuizLoadedSrcs.has(resolvedSrc)) { oldScript.remove(); continue; }
      if (Array.from(document.scripts).some(s => s !== oldScript && s.src === resolvedSrc)) { window.NaturawQuizLoadedSrcs.add(resolvedSrc); oldScript.remove(); continue; }
      const externalScript = document.createElement('script');
      for (const { name, value } of Array.from(oldScript.attributes)) externalScript.setAttribute(name, value);
      externalScript.src = resolvedSrc;
      externalScript.async = false;
      await new Promise((resolve) => {
        externalScript.onload = () => { window.NaturawQuizLoadedSrcs.add(resolvedSrc); resolve(); };
        externalScript.onerror = () => { resolve(); };
        document.head.appendChild(externalScript);
        oldScript.remove();
      });
    }
  } 

  getAssetsBaseUrl() {
    if (this.assetsBaseUrl) return this.assetsBaseUrl;
    const quizModalScript = Array.from(document.scripts).find(s => s.src && s.src.includes('quiz-modal.js'));
    if (quizModalScript) {
      try {
        const url = new URL(quizModalScript.src, document.baseURI);
        this.assetsBaseUrl = url.href.replace(/quiz-modal\.js([?#].*)?$/, '');
        return this.assetsBaseUrl;
      } catch {}
    }
    return '';
  }

  hookCloseButton() {
    const closeButton = this.contentTarget.querySelector('.quiz-header__close');
    if (!closeButton) return;
    closeButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.close();
    }, { once: false });
  }

  close() {
    document.body.classList.remove('quiz-modal-open');
    
    // Restore body styles and scroll position
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    
    if (!this.modal) return;
    this.modal.hide();
    
    // Restore scroll position
    if (typeof this.savedScrollTop === 'number') {
      window.scrollTo(0, this.savedScrollTop);
    }
  }

  onModalClosed() {
    const manager = this.contentTarget.querySelector('quiz-manager');
    if (manager && manager.sessionManager) {
      try {
        // manager.sessionManager.clearSession();
      } catch(e) {}
    }
    this.contentTarget.innerHTML = '';
    this.loadedOnce = false;
    
    // Ensure scroll position is restored
    if (typeof this.savedScrollTop === 'number') {
      window.scrollTo(0, this.savedScrollTop);
    }
  }

  onBodyKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  try {
    window.NaturawQuizModal = new QuizModalController();
    window.NaturawQuizModal.init();
  } catch (e) {
    console.error('[QuizModal] init error', e);
  }
});


